import type { ApiClient } from '@carbonwebmail/api-client'
import type { ApiKeyDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * Keys that let a program act for this organisation.
 *
 * The minted key is held here and nowhere else — not in storage, not in a
 * query parameter, not re-fetched. It exists in this hook's state until the
 * page is left, which is the honest expression of "shown once": nothing on
 * the server can produce it again, so nothing here should pretend it can.
 */

export type ApiKeysState = AsyncState<readonly ApiKeyDto[]>

export function useApiKeys({ client }: { readonly client: ApiClient }) {
  // Unwrapped here rather than on the screen: the envelope is a fact about the
  // response, and a view that had to reach through it would be the only thing
  // in the product that knew about it.
  const load = useCallback(async () => (await client.apiKeys()).keys, [client])
  const { state, reload } = useLoaded<readonly ApiKeyDto[]>(
    load,
    'We could not load your API keys. Please try again in a moment.',
  )

  const [minted, setMinted] = useState<string | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const create = useCallback(
    async (label: string) => {
      setProblem(undefined)
      setBusy(true)
      try {
        const created = await client.createApiKey({ label })
        setMinted(created.secret)
        await reload()
        return true
      } catch {
        setProblem('We could not create that key. Please try again in a moment.')
        return false
      } finally {
        setBusy(false)
      }
    },
    [client, reload],
  )

  const revoke = useCallback(
    async (id: string) => {
      setProblem(undefined)
      setBusy(true)
      try {
        await client.revokeApiKey(id)
        // The key on screen is about to become irrelevant, and leaving it there
        // beside a list where it now reads "revoked" would be confusing.
        setMinted(undefined)
        await reload()
      } catch {
        setProblem('We could not revoke that key. Please try again in a moment.')
      } finally {
        setBusy(false)
      }
    },
    [client, reload],
  )

  /** Dismissing the key, once somebody says they have copied it. */
  const forget = useCallback(() => setMinted(undefined), [])

  return { state, minted, problem, busy, create, revoke, forget, retry: reload }
}
